import type { FormEvent } from 'react';
import { useState } from 'react';
import { setReferenceScore } from '../repositories/qualityScoringRepository';
import { TIER_COLORS } from '../utils/scoreColors';
import { scoreBadgeStyle } from '../utils/scoreBadge';
import { ModalOverlay } from './ModalOverlay';

export function AdminScoreBadge({
  articleId,
  score,
  onUpdate,
  label,
}: {
  articleId: string;
  score: number | null;
  onUpdate: (articleId: string, score: number | null) => void;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pendingScore, setPendingScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);



  const closeModal = () => {
    if (saving) return;
    setOpen(false);
    setPendingScore(null);
    setComment('');
  };

  const openModal = () => {
    setPendingScore(score);
    setComment('');
    setOpen(true);
  };

  const handleConfirm = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pendingScore == null) return;

    setSaving(true);
    try {
      await setReferenceScore(articleId, pendingScore, comment.trim() || undefined);
      onUpdate(articleId, pendingScore);
      closeModal();
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await setReferenceScore(articleId, null);
      onUpdate(articleId, null);
      closeModal();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={openModal}
        style={{
          ...scoreBadgeStyle(score),
          border: '1px solid ' + (score != null ? 'transparent' : 'var(--gray-700)'),
          cursor: 'pointer',
        }}
      >
        {label ? `${label}${score ?? '—'}` : (score != null ? score : '—')}
      </button>
      {open && (
        <ModalOverlay onClose={closeModal} className="score-feedback-modal admin-score-modal">
            <div className="score-feedback-modal-title">
              {label ? `${label} score` : 'Reference score'}
            </div>
            <div className="admin-score-modal-copy">
              Pick the manual score for this article and optionally record a justification.
            </div>
            <div className="admin-score-picker" role="group" aria-label="Reference score">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  disabled={saving}
                  onClick={() => setPendingScore(value)}
                  className={`admin-score-picker-btn${value === pendingScore ? ' selected' : ''}`}
                  style={{ background: TIER_COLORS[value] }}
                >
                  {value}
                </button>
              ))}
            </div>
            <div className="admin-score-modal-status">
              <span>Current: {score != null ? `${label ?? ''}${score}` : 'unset'}</span>
              {pendingScore != null && pendingScore !== score && (
                <span>New: {label ?? ''}{pendingScore}</span>
              )}
            </div>
            <form onSubmit={handleConfirm}>
              <textarea
                className="score-feedback-comment admin-score-comment"
                placeholder="Justification for this manual score change (optional)"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={4}
                autoFocus
              />
              <div className="score-feedback-modal-actions">
                <button
                  type="submit"
                  className="primary-button"
                  disabled={saving || pendingScore == null}
                >
                  {saving ? 'Saving...' : 'Save score'}
                </button>
                {score != null && (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={handleClear}
                    disabled={saving}
                  >
                    Clear score
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
        </ModalOverlay>
      )}
    </>
  );
}
